import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { toast } from 'sonner'
import type {} from '@redux-devtools/extension' // required for devtools typing
import type {
  Outline,
  OutlineSection,
  SectionStatus,
  ContextPack,
  DraftingSession,
  PreflightWarning,
} from '@/lib/api'
import {
  getArtifactOutline,
  updateArtifactOutline,
  getArtifactSectionStatus,
  compileContextPack,
  getDraftingSession,
} from '@/lib/api'
import { useUnitBriefStore } from '@/stores/unit-brief-store'

// ===================
// Optimistic concurrency (the 409 contract — outline spec §3.2.1)
// ===================

/**
 * The outline PATCH now carries a compare-and-swap version, so an ordinary
 * manual edit CAN fail — and every caller here historically swallowed failure.
 * A dropped edit would be strictly worse than the clobbering the CAS prevents,
 * so a conflict is never terminal on the first attempt: refetch, rebase the
 * pending change onto the server's copy, retry once. Only a second conflict is
 * surfaced, and it is surfaced loudly.
 *
 * Structural rather than `instanceof ApiRequestError`: many suites mock
 * `@/lib/api` wholesale, which would leave the class undefined here and make
 * `instanceof` throw inside the recovery path — the one path that must not.
 */
function isVersionConflict(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { statusCode?: unknown }).statusCode === 409
  )
}

/**
 * Three-way merge of an outline's section array, keyed by `sectionKey`.
 *
 * `base` is the last copy both sides agreed on (the last server response),
 * `local` is what the user is trying to save, `remote` is what the server holds
 * now.
 *
 * Per section, the side that actually changed it wins; a section present on one
 * side only is an addition when it was absent from `base` (kept) and a deletion
 * by the other side when it was present (dropped). Sections accepted remotely
 * while the user was editing therefore survive rather than being clobbered.
 *
 * Order is dictated by ONE side, not by local unconditionally: local drives it
 * only when the user demonstrably reordered (or there is no base to compare
 * against), otherwise remote does. An unconditional local order meant any
 * ordinary local save — a retitle, an add — silently reverted a collaborator's
 * reorder, and shunted remotely inserted sections to the end instead of leaving
 * them where the server put them.
 */
export function rebaseOutline(
  base: Outline | null,
  local: Outline,
  remote: Outline
): Outline {
  const baseByKey = new Map((base?.sections ?? []).map((s) => [s.sectionKey, s]))
  const localByKey = new Map(local.sections.map((s) => [s.sectionKey, s]))
  const remoteByKey = new Map(remote.sections.map((s) => [s.sectionKey, s]))

  /** The winning copy of a key, or null when the section should not survive. */
  function resolve(sectionKey: string): OutlineSection | null {
    const localSection = localByKey.get(sectionKey)
    const remoteSection = remoteByKey.get(sectionKey)
    const baseSection = baseByKey.get(sectionKey)

    if (localSection && remoteSection) {
      // Present on both sides — the side that actually changed it wins. Without
      // this an untouched stale copy would silently revert the other writer.
      const touchedLocally =
        baseSection === undefined ||
        JSON.stringify(baseSection) !== JSON.stringify(localSection)
      return touchedLocally ? localSection : remoteSection
    }
    // Present on one side only: it is an addition if it was not there at base,
    // and a deletion by the other side if it was.
    if (localSection) return baseSection ? null : localSection
    if (remoteSection) return baseSection ? null : remoteSection
    return null
  }

  // Did the local user actually reorder? Compare the two orders over the keys
  // each side shares with base, so a plain addition or deletion — which shifts
  // positions without expressing any order intent — does not read as a reorder.
  const commonInBase = (base?.sections ?? [])
    .map((s) => s.sectionKey)
    .filter((key) => localByKey.has(key))
  const commonInLocal = local.sections
    .map((s) => s.sectionKey)
    .filter((key) => baseByKey.has(key))
  const localReordered = commonInBase.some((key, i) => key !== commonInLocal[i])

  // Which side dictates the resulting order. Local order wins ONLY when the user
  // demonstrably reordered: making it win unconditionally means any local save —
  // a retitle, an add — silently reverts a collaborator's reorder, which is the
  // far more likely case. With no base the question is unanswerable, so keep the
  // order the user can actually see. A remote-driven order also keeps a remotely
  // inserted section in its remote position instead of shunting it to the end.
  const localDrivesOrder = base === null || localReordered
  const driver = localDrivesOrder ? local.sections : remote.sections
  const follower = localDrivesOrder ? remote.sections : local.sections

  const merged: OutlineSection[] = []
  const visited = new Set<string>()

  for (const { sectionKey } of [...driver, ...follower]) {
    if (visited.has(sectionKey)) continue
    visited.add(sectionKey)
    const resolved = resolve(sectionKey)
    if (resolved) merged.push(resolved)
  }

  return { ...remote, ...local, sections: merged }
}

// ===================
// Types
// ===================

interface DraftingState {
  // Outline state
  outline: Outline | null
  outlineVersion: number
  /**
   * The outline exactly as the server last returned it, at `outlineVersion`.
   * `outline` diverges from this the moment the user edits optimistically; the
   * pair is the merge base a 409 retry rebases against (§3.2.1).
   */
  lastSyncedOutline: Outline | null
  sectionStatuses: SectionStatus[]

  // Session state
  currentSession: DraftingSession | null
  selectedSectionKey: string | null

  // Context pack state
  contextPack: ContextPack | null
  preflightWarnings: PreflightWarning[]
  canProceed: boolean

  // UI state
  isLoadingOutline: boolean
  isLoadingSession: boolean
  isCompilingContext: boolean
  isSavingOutline: boolean
  error: string | null

  // Artifact reference
  currentArtifactId: string | null

  // Actions - Outline
  fetchOutline: (artifactId: string) => Promise<void>
  saveOutline: (artifactId: string, outline: Outline) => Promise<void>
  addSection: (section: OutlineSection) => void
  addSectionAndSave: (artifactId: string, section: OutlineSection) => Promise<boolean>
  updateSection: (sectionKey: string, updates: Partial<OutlineSection>) => void
  updateSectionAndSave: (artifactId: string, sectionKey: string, updates: Partial<OutlineSection>) => Promise<boolean>
  removeSection: (sectionKey: string) => void
  reorderSections: (fromIndex: number, toIndex: number) => void

  // Actions - Status
  fetchSectionStatuses: (artifactId: string) => Promise<void>

  // Actions - Session
  fetchSession: (artifactId: string, sectionKey: string) => Promise<void>
  selectSection: (sectionKey: string | null) => void

  // Actions - Context
  compileContext: (
    artifactId: string,
    sectionKey: string,
    selectedEvidence?: string[]
  ) => Promise<void>
  clearContext: () => void

  // Actions - General
  clearError: () => void
  reset: () => void
}

// ===================
// Store
// ===================

export const useDraftingStore = create<DraftingState>()(
  devtools(
    (set) => ({
      // Initial state
      outline: null,
      outlineVersion: 0,
      lastSyncedOutline: null,
      sectionStatuses: [],
      currentSession: null,
      selectedSectionKey: null,
      contextPack: null,
      preflightWarnings: [],
      canProceed: true,
      isLoadingOutline: false,
      isLoadingSession: false,
      isCompilingContext: false,
      isSavingOutline: false,
      error: null,
      currentArtifactId: null,

      // ===================
      // Outline Actions
      // ===================

      fetchOutline: async (artifactId: string) => {
        try {
          set({ isLoadingOutline: true, error: null, currentArtifactId: artifactId })
          const response = await getArtifactOutline(artifactId)
          // Another document was opened while this was in flight: its fetch
          // owns the stores now. Landing this one would leave the previous
          // document's outline and briefs under the new `currentArtifactId`.
          if (useDraftingStore.getState().currentArtifactId !== artifactId) return
          set({
            outline: response.outline,
            lastSyncedOutline: response.outline,
            outlineVersion: response.outlineVersion,
            isLoadingOutline: false,
          })
          // Hydrate the per-section brief store from the bundled payload (Scoped
          // Brief P1). One point serves both the multi-section editor and the
          // single-document editor — the `__document__` key flows identically.
          useUnitBriefStore.getState().hydrate(artifactId, response.unitBriefs ?? [])
        } catch (error) {
          if (useDraftingStore.getState().currentArtifactId !== artifactId) return
          set({
            // Drop whatever outline was loaded before. The store outlives a
            // route change, so a failed load would otherwise leave the PREVIOUS
            // artifact's outline in place — the editor then reads it as a live
            // outline and shows "Section not found" instead of the load-error
            // screen this error is meant to raise.
            outline: null,
            lastSyncedOutline: null,
            outlineVersion: 0,
            error: error instanceof Error ? error.message : 'Failed to fetch outline',
            isLoadingOutline: false,
          })
        }
      },

      saveOutline: async (artifactId: string, outline: Outline) => {
        try {
          set({ isSavingOutline: true, error: null })
          const response = await saveOutlineWithCas(artifactId, outline)
          set({
            outline: response.outline,
            lastSyncedOutline: response.outline,
            outlineVersion: response.outlineVersion,
            isSavingOutline: false,
          })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to save outline',
            isSavingOutline: false,
          })
          throw error
        }
      },

      addSection: (section: OutlineSection) => {
        set((state) => {
          if (!state.outline) {
            return { outline: { sections: [section] } }
          }
          return {
            outline: {
              ...state.outline,
              sections: [...state.outline.sections, section],
            },
          }
        })
      },

      // Optimistically append a section, persist it, and roll the append back
      // if the write fails — the mirror of updateSectionAndSave for the
      // quick-add path. Save failures deliberately do NOT set `error`: that
      // field drives the editors' full-page error screens, which are for load
      // failures (nothing to render). A failed quick-add must leave the editor
      // usable, so callers surface the boolean as a toast instead.
      addSectionAndSave: async (artifactId: string, section: OutlineSection): Promise<boolean> => {
        const { outline } = useDraftingStore.getState()
        if (!outline) return false

        const updatedOutline = {
          ...outline,
          sections: [...outline.sections, section],
        }

        set({ outline: updatedOutline, isSavingOutline: true })

        try {
          const response = await saveOutlineWithCas(artifactId, updatedOutline)
          set({
            outline: response.outline,
            lastSyncedOutline: response.outline,
            outlineVersion: response.outlineVersion,
            isSavingOutline: false,
          })
          return true
        } catch {
          set({ outline, isSavingOutline: false })
          return false
        }
      },

      updateSection: (sectionKey: string, updates: Partial<OutlineSection>) => {
        set((state) => {
          if (!state.outline) return state
          return {
            outline: {
              ...state.outline,
              sections: state.outline.sections.map((s) =>
                s.sectionKey === sectionKey ? { ...s, ...updates } : s
              ),
            },
          }
        })
      },

      // Returns whether the PATCH succeeded so callers can gate side-effects
      // (e.g. marking a plan stale) on observed success — this action swallows
      // its own error and resolves, so a resolved await alone is not success.
      // Note the deliberate asymmetry with addSectionAndSave: this one still
      // records `error` (long-standing behaviour other pages surface), so every
      // caller must toast off the boolean rather than rely on that field —
      // SectionEditorPage no longer renders a full-page screen for it.
      //
      // A section that isn't in the outline is a failure, not a no-op success:
      // a single document's outline is `{ sections: [] }` (no `__document__`
      // entry), so the patch would change nothing and the caller would report
      // e.g. a linked win theme that was never stored. Like addSectionAndSave,
      // this does not set `error` — it isn't a load failure.
      updateSectionAndSave: async (artifactId: string, sectionKey: string, updates: Partial<OutlineSection>): Promise<boolean> => {
        const { outline } = useDraftingStore.getState()
        if (!outline) return false
        if (!outline.sections.some((s) => s.sectionKey === sectionKey)) return false

        const updatedOutline = {
          ...outline,
          sections: outline.sections.map((s) =>
            s.sectionKey === sectionKey ? { ...s, ...updates } : s
          ),
        }

        set({ outline: updatedOutline })

        try {
          set({ isSavingOutline: true, error: null })
          const response = await saveOutlineWithCas(artifactId, updatedOutline)
          set({
            outline: response.outline,
            lastSyncedOutline: response.outline,
            outlineVersion: response.outlineVersion,
            isSavingOutline: false,
          })
          return true
        } catch (error) {
          set({
            // Roll the optimistic mutation back — it never reached the server.
            // Leaving it in place makes a retry read its own unsaved change and
            // skip the write as a no-op de-dupe, so the caller sees success with
            // nothing persisted (bit acceptPlanSuggestion's theme branch).
            outline,
            error: error instanceof Error ? error.message : 'Failed to save outline',
            isSavingOutline: false,
          })
          return false
        }
      },

      removeSection: (sectionKey: string) => {
        set((state) => {
          if (!state.outline) return state
          return {
            outline: {
              ...state.outline,
              sections: state.outline.sections.filter((s) => s.sectionKey !== sectionKey),
            },
            selectedSectionKey:
              state.selectedSectionKey === sectionKey ? null : state.selectedSectionKey,
          }
        })
      },

      reorderSections: (fromIndex: number, toIndex: number) => {
        set((state) => {
          if (!state.outline) return state
          const sections = [...state.outline.sections]
          const [removed] = sections.splice(fromIndex, 1)
          if (removed) {
            sections.splice(toIndex, 0, removed)
          }
          return {
            outline: {
              ...state.outline,
              sections,
            },
          }
        })
      },

      // ===================
      // Status Actions
      // ===================

      fetchSectionStatuses: async (artifactId: string) => {
        try {
          const response = await getArtifactSectionStatus(artifactId)
          set({ sectionStatuses: response.sections })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch section statuses',
          })
        }
      },

      // ===================
      // Session Actions
      // ===================

      fetchSession: async (artifactId: string, sectionKey: string) => {
        try {
          set({ isLoadingSession: true, error: null })
          const response = await getDraftingSession(artifactId, sectionKey)
          set({
            currentSession: response.session,
            selectedSectionKey: sectionKey,
            isLoadingSession: false,
          })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch session',
            isLoadingSession: false,
          })
        }
      },

      selectSection: (sectionKey: string | null) => {
        set({
          selectedSectionKey: sectionKey,
          currentSession: null,
          contextPack: null,
          preflightWarnings: [],
          canProceed: true,
        })
      },

      // ===================
      // Context Actions
      // ===================

      compileContext: async (
        artifactId: string,
        sectionKey: string,
        selectedEvidence?: string[]
      ) => {
        try {
          set({ isCompilingContext: true, error: null })
          const response = await compileContextPack(artifactId, sectionKey, selectedEvidence)
          set({
            contextPack: response.contextPack,
            preflightWarnings: response.preflight.warnings,
            canProceed: response.preflight.canProceed,
            isCompilingContext: false,
          })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to compile context',
            isCompilingContext: false,
          })
          throw error
        }
      },

      clearContext: () => {
        set({
          contextPack: null,
          preflightWarnings: [],
          canProceed: true,
        })
      },

      // ===================
      // General Actions
      // ===================

      clearError: () => {
        set({ error: null })
      },

      reset: () => {
        set({
          outline: null,
          outlineVersion: 0,
          lastSyncedOutline: null,
          sectionStatuses: [],
          currentSession: null,
          selectedSectionKey: null,
          contextPack: null,
          preflightWarnings: [],
          canProceed: true,
          isLoadingOutline: false,
          isLoadingSession: false,
          isCompilingContext: false,
          isSavingOutline: false,
          error: null,
          currentArtifactId: null,
        })
      },
    }),
    { name: 'drafting-store' }
  )
)

// ===================
// CAS write path (§3.2.1)
// ===================

/**
 * PATCH the outline guarded by the version the store last saw, and honour the
 * 409 contract: on a lost race refetch the server's outline, rebase the pending
 * change onto it, and retry **once** against the fresh version.
 *
 * A second conflict is genuinely concurrent editing, and it is toasted here
 * rather than at the call sites because every one of them swallows failure —
 * `autoSaveOutline` is `void saveOutline(...)`, the quick-add and drag handlers
 * discard the result, and `updateSectionAndSave`'s `error` is no longer rendered
 * by SectionEditorPage. A silently dropped edit is worse than the clobbering the
 * CAS exists to prevent, so the toast is the one guaranteed surface. The error
 * is still rethrown so callers can roll their optimistic mutation back.
 */
async function saveOutlineWithCas(
  artifactId: string,
  desired: Outline
): Promise<{ outline: Outline; outlineVersion: number }> {
  const { outlineVersion, lastSyncedOutline } = useDraftingStore.getState()

  try {
    return await updateArtifactOutline(artifactId, desired, outlineVersion)
  } catch (error) {
    if (!isVersionConflict(error)) throw error

    try {
      const fresh = await getArtifactOutline(artifactId)
      const rebased = rebaseOutline(lastSyncedOutline, desired, fresh.outline)
      return await updateArtifactOutline(artifactId, rebased, fresh.outlineVersion)
    } catch (retryError) {
      toast.error(
        isVersionConflict(retryError)
          ? 'Someone else is editing this outline. Your change was not saved — reload and try again.'
          : 'Could not save the outline. Your change was not saved — please try again.'
      )
      throw retryError
    }
  }
}
